'use client';

import { useIsomorphicEffect, useViewportSize } from '@mantine/hooks';
import { useState } from 'react';

/**
 * Mantine breakpoints
 *  xs:  '36em' or '576px',  // phone                         [isMobile]
 * 	sm:  '48em' or '768px',  // tablet                        [isMobile]
 * 	md:  '62em' or '992px',  // laptop                        [isBigScreen]
 * 	lg:  '75em' or '1200px', // desktop                       [isBigScreen]
 * 	xl:  '88em' or '1408px', // ultra-wide display (UWD)      [isBigScreen]
 */

/**
 * Calculates the type of window based on the width.
 *
 * @param {number} width - The width of the window.
 * @returns {string} - The type of window ('phone', 'tablet', 'laptop', 'desktop', 'uwd').
 */
const calculateWindowType = (width: number): string => {
	if (width < 576) {
		return 'phone';
	}
	if (width < 992) {
		return 'tablet';
	}
	if (width < 1200) {
		return 'laptop';
	}
	if (width < 1408) {
		return 'desktop';
	}
	return 'uwd';
};

const isStandalone = () => {
	if (typeof window === 'undefined') {
		return null;
	}

	const isStandaloneMedia = window.matchMedia('(display-mode: standalone)').matches;
	if (document.referrer.startsWith('android-app://')) {
		return true; // Trusted web app
	}
	if (!!('standalone' in window.navigator && window.navigator.standalone) || isStandaloneMedia) {
		return true;
	}
	return false;
};

/**
 * @property {boolean} isPhone - Indicates if the device is a phone.
 * @property {boolean} isTablet - Indicates if the device is a tablet.
 * @property {boolean} isMobile - Indicates if the device is mobile (phone or tablet).
 * @property {boolean} isLaptop - Indicates if the device is a laptop.
 * @property {boolean} isDesktop - Indicates if the device is a desktop.
 * @property {boolean} isUWD - Indicates if the device is an ultra-wide display.
 * @property {boolean} isBigScreen - Indicates if the device is a big screen (laptop, desktop, or UWD).
 * @property {string} deviceType - The type of device.
 * @property {string} browser - The name of the browser.
 */
export type Device = {
	isPhone: boolean;
	isTablet: boolean;
	isMobile: boolean;
	isLaptop: boolean;
	isDesktop: boolean;
	isUWD: boolean;
	isBigScreen: boolean;
	isStandalone: boolean | null;
	deviceType: string;
	browser: 'edge' | 'chrome' | 'firefox' | 'safari' | 'samsung' | 'unknown' | null;
};

/**
 * Custom hook to get the device information based on the window size and browser.
 *
 * @returns {Device} - The device information.
 * @public
 */
export const useDevice = (): Device => {
	const { width } = useViewportSize();

	/**
	 * Gets the name of the browser.
	 *
	 * @returns {string} - The name of the browser.
	 */
	const getBrowserName = () => {
		if (typeof window === 'undefined') {
			return null;
		}

		const userAgent = navigator.userAgent;
		if (userAgent.indexOf('SamsungBrowser') > -1) {
			return 'samsung';
		}
		if (userAgent.indexOf('Edg') > -1) {
			return 'edge';
		}
		if (userAgent.indexOf('Chrome') > -1 || userAgent.indexOf('CriOS') > -1) {
			return 'chrome';
		}
		if (userAgent.indexOf('Firefox') > -1 || userAgent.indexOf('FxiOS') > -1) {
			return 'firefox';
		}
		if (userAgent.indexOf('Safari') > -1) {
			return 'safari';
		}
		return 'unknown';
	};

	const [device, setDevice] = useState<Device>({
		isPhone: true,
		isTablet: false,
		isMobile: true,
		isLaptop: false,
		isDesktop: false,
		isUWD: false,
		isBigScreen: false,
		isStandalone: isStandalone(),
		deviceType: 'unknown',
		browser: getBrowserName(),
	});

	/**
	 * Updates the device state based on the window width.
	 *
	 * @param {number} width - The width of the window.
	 */
	const changeDevice = (width: number): void => {
		const type = calculateWindowType(width);
		setDevice({
			isPhone: type === 'phone',
			isTablet: type === 'tablet',
			isMobile: type === 'phone' || type === 'tablet',
			isLaptop: type === 'laptop',
			isDesktop: type === 'desktop',
			isUWD: type === 'uwd',
			isBigScreen: type === 'laptop' || type === 'desktop' || type === 'uwd',
			isStandalone: isStandalone(),
			deviceType: type,
			browser: getBrowserName(),
		});
	};

	useIsomorphicEffect(() => {
		changeDevice(width);
	}, [width]);

	return device;
};
